import { Box, Container, Divider, Grid, MenuItem, Paper, Typography } from "@mui/material";
import {
  ButtonBase,
  FormControlBase,
  FormSectionBox,
  FormSectionTitle,
  InputLabelSelect,
  SelectBase,
  TextFieldBase,
} from "../../../styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useContext, useEffect, useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import { MyContext } from "../../../../../Context/MyContext";
import { PedidosContext } from "../../../../../Context/PedidosContext";
import api from "../../../../../Api/apiAxios";

async function getContratoById(id) {
  const resp = await api.get(`/Contrato/${id}`);
  return resp?.data ?? null;
}

async function getDistribuidorById(id) {
  const resp = await api.get(`/Distribuidor/${id}`);
  return resp?.data ?? null;
}

async function getClienteById(id) {
  const resp = await api.get(`/Cliente/${id}`);
  return resp?.data ?? null;
}

export function ModalCancelamento({ contratoId }) {
  const { setMensagemGlobal, setTipoMensagemGlobal } = useContext(MyContext);
  const {
    productVariant,
    motivosCancelamento,
    fetchMotivosCancelamento,
    setModalCancelamentoContrato,
  } = useContext(PedidosContext);

  const idUsuario = Number(window.localStorage.getItem("idUsuario") || 0);

  const [isLoading, setIsLoading] = useState(false);
  const [contrato, setContrato] = useState(null);
  const [distribuidor, setDistribuidor] = useState(null);
  const [cliente, setCliente] = useState(null);

  const contratoIdNum = useMemo(() => Number(contratoId || 0), [contratoId]);

  const validationSchema = Yup.object({
    dataCancelamento: Yup.date()
      .required("Data de cancelamento é obrigatória.")
      .min(subDays(new Date(), 1), "A data de cancelamento deve ser a partir de hoje."),
    motivoCancelamentoOpcao: Yup.number()
      .required("Motivo do cancelamento deve ser selecionado.")
      .min(1, "Motivo do cancelamento deve ser selecionado."),
    motivoCancelamento: Yup.string().nullable(),
    cancelamentoReal: Yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      tipoSistema: productVariant,
      distribuidor: "",
      cliente: "",
      nomeFantasia: "",
      dataAtivacao: "",
      valorContrato: 0,
      numeroSerie: "",
      idPedido: "",
      dataCancelamento: format(new Date(), "yyyy-MM-dd"),
      motivoCancelamentoOpcao: 0,
      motivoCancelamento: "",
      cancelamentoReal: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const id = Number(contrato?.id ?? contrato?.Id ?? contratoIdNum ?? 0);
        if (!id) throw new Error("Contrato inválido.");

        const motivo = encodeURIComponent(values.motivoCancelamento || "");
        const motivoCancelamentoOpcao = Number(values.motivoCancelamentoOpcao || 0);
        const dataCancelamento = encodeURIComponent(values.dataCancelamento);
        const cancelamentoReal = values.cancelamentoReal !== false;

        const usuarioParam = idUsuario ? `&usuarioIdCancelamento=${idUsuario}` : "";

        const ret = await api.put(
          `/Contrato/cancelamento/agendar` +
            `?id=${id}` +
            `&motivo=${motivo}` +
            `&motivoCancelamentoOpcao=${motivoCancelamentoOpcao}` +
            `&dataCancelamento=${dataCancelamento}` +
            `&cancelamentoReal=${cancelamentoReal}` +
            `${usuarioParam}`
        );

        setMensagemGlobal(ret?.data?.message || "Cancelamento registrado com sucesso.");
        setTipoMensagemGlobal("success");
        setModalCancelamentoContrato({ show: false, contrato: {} });
      } catch (err) {
        console.error(err);
        setMensagemGlobal(
          err?.response?.data?.message || err?.message || "Erro ao registrar cancelamento."
        );
        setTipoMensagemGlobal("error");
      }
    },
  });

  useEffect(() => {
    fetchMotivosCancelamento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadAll() {
      if (!contratoIdNum) {
        setContrato(null);
        setDistribuidor(null);
        setCliente(null);
        return;
      }

      try {
        setIsLoading(true);

        const c = await getContratoById(contratoIdNum);
        if (!c) throw new Error("Contrato não encontrado.");

        setContrato(c);

        const distribuidorId = Number(c.distribuidorId ?? c.DistribuidorId ?? 0);
        const clienteId = Number(c.clienteId ?? c.ClienteId ?? 0);

        const [d, cl] = await Promise.all([
          distribuidorId ? getDistribuidorById(distribuidorId) : Promise.resolve(null),
          clienteId ? getClienteById(clienteId) : Promise.resolve(null),
        ]);

        setDistribuidor(d);
        setCliente(cl);

        const dataInicio =
          c.dataInicio ?? c.DataInicio ?? c.dataAtivacao ?? c.DataAtivacao ?? null;

        const dataDeCancelamento =
          c.dataCancelamento !== null && c.dataCancelamento !== undefined
            ? format(new Date(c.dataCancelamento), "yyyy-MM-dd")
            : format(new Date(), "yyyy-MM-dd");

        const distribuidorNome =
          d?.razaoSocial ??
          d?.RazaoSocial ??
          c?.distribuidor?.razaoSocial ??
          c?.DistribuidorRazaoSocial ??
          "";

        const clienteRazaoSocial =
          cl?.razaoSocial ??
          cl?.RazaoSocial ??
          c?.cliente?.razaoSocial ??
          c?.ClienteRazaoSocial ??
          "";

        const clienteNomeFantasia =
          cl?.nomeFantasia ??
          cl?.NomeFantasia ??
          c?.cliente?.nomeFantasia ??
          c?.ClienteNomeFantasia ??
          "";
        
        formik.setValues({
          tipoSistema: productVariant,
          distribuidor: distribuidorNome,
          cliente: clienteRazaoSocial,
          nomeFantasia: clienteNomeFantasia,
          dataAtivacao: dataInicio ? format(new Date(dataInicio), "yyyy-MM-dd") : "",
          valorContrato: Number(c.valorAtual ?? c.ValorAtual ?? c.valor ?? c.Valor ?? 0),
          numeroSerie: c.numeroSerie ?? c.NumeroSerie ?? "",
          idPedido: c.pedidoId ?? c.PedidoId ?? "",
          dataCancelamento: dataDeCancelamento,
          motivoCancelamentoOpcao: c.motivoCancelamentoOpcao ?? c.MotivoCancelamentoOpcao ?? 0,
          motivoCancelamento: c.motivoCancelamento ?? c.MotivoCancelamento ?? "",
          cancelamentoReal:
            c.cancelamentoReal !== undefined
              ? c.cancelamentoReal
              : c.CancelamentoReal !== undefined
              ? c.CancelamentoReal
              : true,
        });
      } catch (err) {
        console.error(err);
        setContrato(null);
        setDistribuidor(null);
        setCliente(null);

        setMensagemGlobal(
          err?.response?.data?.message || err?.message || "Erro ao carregar contrato."
        );
        setTipoMensagemGlobal("error");
      } finally {
        setIsLoading(false);
      }
    }

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoIdNum, productVariant]);

  const disabledForm = isLoading || !contratoIdNum || !contrato;

  return (
    <Container sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        sx={{
          p: 2,
          width: "80%",
          alignSelf: "center",
          position: "fixed",
          top: 80,
          overflow: "auto",
          zIndex: 1000,
          maxHeight: "80vh",
          backgroundColor: "#f8f9fa",
        }}
        elevation={3}
      >
        <Typography
          variant="h5"
          sx={{
            pb: 1,
            color: "tomato",
            fontWeight: "bold",
            width: "100%",
            textAlign: "center",
          }}
        >
          Cancelar Contrato
        </Typography>

        <Divider />

        {isLoading && (
          <Typography sx={{ mt: 2, textAlign: "center" }}>
            Carregando contrato...
          </Typography>
        )}

        {!isLoading && !contrato && (
          <Typography sx={{ mt: 2, textAlign: "center" }}>
            Não foi possível carregar o contrato.
          </Typography>
        )}

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{ p: 1, mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <FormSectionBox>
            <FormSectionTitle label="Dados do Pedido" />
            <Grid container spacing={2} sx={{ pb: 2, pl: 4, mt: 1, display: "flex" }}>
              <Grid item xs={3}>
                <FormControlBase>
                  <InputLabelSelect id="tipoSistema">Tipo de Sistema</InputLabelSelect>
                  <SelectBase
                    id="tipoSistema"
                    name="tipoSistema"
                    value={formik.values.tipoSistema}
                    width="100%"
                    disabled
                  >
                    <MenuItem value="Acesso">Acesso</MenuItem>
                    <MenuItem value="Folha">Folha</MenuItem>
                    <MenuItem value="Ponto">Ponto</MenuItem>
                  </SelectBase>
                </FormControlBase>
              </Grid>

              <Grid item xs={3}>
                <FormControlBase>
                  <TextFieldBase
                    label="Data de Ativação"
                    name="dataAtivacao"
                    value={formik.values.dataAtivacao}
                    type="date"
                    width="100%"
                    disabled
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={3}>
                <FormControlBase>
                  <TextFieldBase
                    label="ID do Pedido"
                    name="idPedido"
                    value={formik.values.idPedido}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={3}>
                <FormControlBase>
                  <TextFieldBase
                    label="Valor do Contrato (R$)"
                    name="valorContrato"
                    value={formik.values.valorContrato}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={6}>
                <FormControlBase>
                  <TextFieldBase
                    label="Número de Série"
                    name="numeroSerie"
                    value={formik.values.numeroSerie}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={6}>
                <FormControlBase>
                  <InputLabelSelect id="distribuidor">Revenda</InputLabelSelect>
                  <TextFieldBase
                    id="distribuidor"
                    label="Revenda"
                    name="distribuidor"
                    value={formik.values.distribuidor}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={6}>
                <FormControlBase>
                  <InputLabelSelect id="cliente">Cliente</InputLabelSelect>
                  <TextFieldBase
                    id="cliente"
                    label="Cliente"
                    name="cliente"
                    value={formik.values.cliente}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={6}>
                <FormControlBase>
                  <InputLabelSelect id="nomeFantasia">Nome Fantasia</InputLabelSelect>
                  <TextFieldBase
                    id="nomeFantasia"
                    label="Nome Fantasia"
                    name="nomeFantasia"
                    value={formik.values.nomeFantasia}
                    disabled
                    width="100%"
                  />
                </FormControlBase>
              </Grid>
            </Grid>
          </FormSectionBox>

          <FormSectionBox>
            <FormSectionTitle label="Dados do Cancelamento" />
            <Grid container spacing={2} sx={{ pb: 2, px: 4, mt: 1, display: "flex" }}>
              <Grid item xs={4}>
                <FormControlBase>
                  <TextFieldBase
                    label="Data de Cancelamento"
                    name="dataCancelamento"
                    value={formik.values.dataCancelamento}
                    type="date"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.dataCancelamento && Boolean(formik.errors.dataCancelamento)}
                    helperText={formik.touched.dataCancelamento && formik.errors.dataCancelamento}
                    width="100%"
                    disabled={disabledForm}
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={8}>
                <FormControlBase>
                  <InputLabelSelect id="motivoCancelamentoOpcao">Motivo</InputLabelSelect>
                  <SelectBase
                    id="motivoCancelamentoOpcao"
                    name="motivoCancelamentoOpcao"
                    value={formik.values.motivoCancelamentoOpcao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.motivoCancelamentoOpcao && Boolean(formik.errors.motivoCancelamentoOpcao)}
                    helperText={formik.touched.motivoCancelamentoOpcao && formik.errors.motivoCancelamentoOpcao}
                    label="Motivo"
                    width="100%"
                    disabled={disabledForm}
                  >
                    {motivosCancelamento.map((motivo) => (
                      <MenuItem key={motivo.id} value={motivo.id}>
                        {motivo.descricaoMotivo}
                      </MenuItem>
                    ))}
                  </SelectBase>
                </FormControlBase>
              </Grid>

              <Grid item xs={12}>
                <FormControlBase>
                  <TextFieldBase
                    label="Detalhes"
                    name="motivoCancelamento"
                    value={formik.values.motivoCancelamento}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.motivoCancelamento && Boolean(formik.errors.motivoCancelamento)}
                    helperText={formik.touched.motivoCancelamento && formik.errors.motivoCancelamento}
                    width="100%"
                    multiline
                    rows={4}
                    disabled={disabledForm}
                  />
                </FormControlBase>
              </Grid>

              <Grid item xs={12}>
                <FormControlBase>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="checkbox"
                      id="cancelamentoReal"
                      name="cancelamentoReal"
                      checked={formik.values.cancelamentoReal !== false}
                      onChange={(e) => formik.setFieldValue("cancelamentoReal", e.target.checked)}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                      disabled={disabledForm}
                    />
                    <label htmlFor="cancelamentoReal" style={{ cursor: "pointer", userSelect: "none" }}>
                      Cancelamento real (se desmarcado, não será contabilizado no relatório de contratos cancelados)
                    </label>
                  </Box>
                </FormControlBase>
              </Grid>
            </Grid>
          </FormSectionBox>

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, pt: 2 }}>
            <ButtonBase
              variant="outlined"
              onClick={() => setModalCancelamentoContrato({ show: false, contrato: {} })}
            >
              Voltar
            </ButtonBase>

            <ButtonBase onClick={formik.handleSubmit} variant="contained" disabled={disabledForm}>
              Cancelar
            </ButtonBase>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
